(ns common.transaction-process.resolver
  "The transaction-process engine: resolve the recursive field dictionary
   (`common.transaction-process.dictionary`) against a transaction-process entity
   and derive value/visibility/mandatoriness, slot requiredness (tri-state), slot
   completeness, and the unified 'what's missing' gate.

   Each single sub-entity is a named ref read straight off the entity
   (`(get tp slot-kw)`); a homogeneous list is a named ref-many (`(get tp
   list-kw)`); the four dictionary kinds (value / attachment-slot / entity-slot /
   entity-list) are resolved uniformly, recursing through nested templates.

   Pure and `.cljc`: the caller resolves sub-entity refs into entity maps
   (frontend via the session, backend via the db) and passes a TP entity whose
   slot attributes already hold the child entity map(s). No session/db reads here.
   Uses the rule DSL (`common.visibility` / `common.mandatory`) and the done?
   predicates. Presentation order is the layout layer's concern — this
   engine never imposes one."
  (:require
   [common.mandatory :as mandatory]
   [common.transaction-process.dictionary :as dict]
   [common.util :as cu]
   [common.visibility :as visibility]))

(defn value-lookup
  "The rule-evaluation map for a level: any static `rule-context` keys under the
   `parent-lookup` (ancestor levels) under this entity's own data — so a rule at
   any depth can read its ancestors' fields while the level's own values win on
   collisions. Carries the root process data down, so a decision slot reads the
   TP's needed-field (or its stage) with no special wiring."
  [{:keys [rule-context parent-lookup entity-data]}]
  (merge rule-context parent-lookup entity-data))

;; value-field / attachment-slot

(defn attachment-slot-value
  "Attachments tagged with this slot-id (= the dictionary key) in their
   `:common/system-kws` — the 'document slot' resolution."
  [slot-kw entity-data]
  (->> (:common/attachments entity-data)
       (filterv #(some #{slot-kw} (:common/system-kws %)))))

(defn field-value
  "Raw value of a value-field / attachment-slot entry. Attachment-slots resolve
   over `:common/attachments`; everything else is a plain attribute lookup."
  [{:keys [item-kw entry entity-data]}]
  (if (= :field.kind/attachment-slot (dict/entry-kind entry))
    (attachment-slot-value item-kw entity-data)
    (get entity-data item-kw)))

(defn field-visible?
  [entry lookup]
  (visibility/calc-visibility lookup
                              (:field/visibility-rules entry)
                              (or (:field/visibility entry) :visibility/always)))

(defn field-mandatory?
  [{:keys [entry visible? lookup]}]
  (boolean
   (mandatory/calc-mandatoriness
    visible? lookup
    (or (:field/mandatoriness entry) :mandatory/optional)
    (:field/mandatoriness-rules entry))))

(defn resolve-field
  "Resolve one value-field / attachment-slot entry to the canonical map every
   output target consumes."
  [{:keys [item-kw entry entity-data lookup]}]
  (let [visible?  (field-visible? entry lookup)
        attach?   (= :field.kind/attachment-slot (dict/entry-kind entry))
        value     (field-value {:item-kw     item-kw
                                :entry       entry
                                :entity-data entity-data})]
    {:item/kind        (dict/entry-kind entry)
     :item-kw          item-kw
     :entry            entry
     :label            (or (:slot/label entry) (:field/label entry))
     :data-type        (:field/data-type entry)
     :attachment-slot? attach?
     :value            value
     :visible?         visible?
     :mandatory?       (field-mandatory? {:entry    entry
                                          :visible? visible?
                                          :lookup   lookup})
     :present?         (if attach? (boolean (seq value)) (not (cu/blank-value? value)))}))

;; entity-slot requiredness / visibility / completeness

(defn slot-required?
  "Tri-state requiredness of an entity-slot: true (needed) / false (not needed) /
   nil (undecided). `:from-field` reads a per-process field from `lookup` and maps
   it via `:true-value`/`:false-value`; any other value - including an unset
   field - falls back to the static `:default?`, as does a slot with no
   `:from-field`. So nil comes from an absent `:default?`, not from an unset
   field on its own."
  [entry lookup]
  (let [{:keys [from-field true-value false-value default?]} (:slot/required entry)]
    (if from-field
      (let [v (get lookup from-field)]
        (cond (= v true-value)  true
              (= v false-value) false
              :else             default?))
      default?)))

(defn slot-editable?
  "Whether the slot's requiredness selector is user-editable (decisions on the
   Reviewing stage). Attestations/declarations default to non-editable."
  [entry]
  (boolean (get-in entry [:slot/required :editable?])))

(defn slot-visible?
  [entry lookup]
  (visibility/calc-visibility lookup
                              (:slot/visibility-rules entry)
                              (or (:slot/visibility entry) :visibility/always)))

(defn slot-in-scope?
  "An entity-slot is in scope when its visibility rule passes or an existing
  instance is retained while hidden. Existing instances are retained by default
  for historical data; a template can set `:slot/retain-when-hidden? false` for
  dependent data that must leave scope with its controlling field."
  [entry lookup has-instance?]
  (or (slot-visible? entry lookup)
      (and has-instance?
           (not= false (:slot/retain-when-hidden? entry)))))

(defn slot-category
  "An entity-slot's gating category, from its `:slot/entity-type`. Attestations
   and declarations are their shared types; everything else (the distinct
   `*-decision` types) is a decision."
  [entry]
  (case (:slot/entity-type entry)
    :entity.type/attestation :attestations
    :entity.type/declaration :declarations
    :decisions))

(defn category-done?
  "Completeness of a resolved sub-entity instance for a gating `category`:
   a declaration is done when not draft
   and either completed or not-applicable; an attestation/decision is done when
   not draft. Dispatched on the category (taken from the slot config, which always
   carries it) rather than the instance's `:entity/type`, so a partial instance
   map can never be misclassified. nil instance ⇒ nil."
  [category instance]
  (when instance
    (case category
      :declarations (boolean (and (not (:declaration/draft? instance))
                                  (or (:declaration/completed? instance)
                                      (:declaration/not-applicable? instance))))
      :attestations (not (:attestation/draft? instance))
      :decisions    (not (:decision/draft? instance))
      (some? instance))))

(defn slot-done?
  "Whether the entity-slot's current instance is complete, per the slot's
   category. nil instance ⇒ nil."
  [entry instance]
  (category-done? (slot-category entry) instance))

(defn entity-slots
  "The top-level entity-slot entries of a dictionary, as `[item-kw entry]` pairs."
  [dictionary]
  (filter (fn [[_ e]] (= :field.kind/entity-slot (dict/entry-kind e))) dictionary))

(defn slots-by-category
  "Top-level entity-slots of `category`, each `{:slot-kw :entry :instance}` where
   the instance is the resolved child read off `tp` (or nil). A named ref
   needs no matching, just a lookup."
  [dictionary tp category]
  (->> (entity-slots dictionary)
       (filter (fn [[_ e]] (= category (slot-category e))))
       (mapv (fn [[k e]] {:slot-kw k :entry e :instance (get tp k)}))))

(defn attestation-outcome-option
  "The configured option map for an attestation slot's current outcome."
  [{:keys [entry instance]}]
  (let [outcome (:attestation/outcome instance)]
    (->> (get-in entry [:slot/template :attestation/outcome :field/options])
         (filter (fn [option] (= outcome (:value option))))
         first)))

(defn danger-attestation?
  "Whether a live attestation instance has an outcome configured as danger."
  [resolved-slot]
  (let [instance (:instance resolved-slot)]
    (boolean
     (and instance
          (not (:common/disabled? instance))
          (= :danger (:variant (attestation-outcome-option resolved-slot)))))))

(defn danger-attestations?
  "Whether any attestation slot in the process currently has a danger outcome."
  [dictionary tp]
  (->> (slots-by-category dictionary tp :attestations)
       (some danger-attestation?)
       boolean))

(defn slot-instance
  "The resolved sub-entity (entity-slot) or seq (entity-list) under `slot-kw`."
  [tp slot-kw]
  (get tp slot-kw))

(defn missing-value-fields
  "Value-field entries of a `template` that are visible, mandatory, and blank in
   `instance`. `lookup` carries the rule-evaluation context (typically the merged
   process + instance data). `field-enabled?` (optional `(item-kw -> boolean)`)
   gates a field out (e.g. per reservation type). Returns `[{:item-kw :label}]`."
  [{:keys [template instance lookup field-enabled?]}]
  (let [field-enabled? (or field-enabled? (constantly true))]
    (->> template
         (filter (fn [[_ e]] (contains? #{:field.kind/value :field.kind/attachment-slot}
                                        (dict/entry-kind e))))
         (keep (fn [[item-kw entry]]
                 (let [r (resolve-field {:item-kw item-kw :entry entry
                                         :entity-data instance :lookup lookup})]
                   (when (and (field-enabled? item-kw)
                              (:visible? r) (:mandatory? r) (not (:present? r)))
                     {:item-kw item-kw :label (:label r)}))))
         vec)))

;; stage gating

(defn incomplete-required-slots
  "Top-level entity-slots that are in scope, resolve required (true), and whose
   instance is not done. `tp` is the TP entity with slot refs resolved to child
   maps; `lookup` is the merged process attrs + rule-context (vehicle keys etc.).
   Returns `[{:category :slot-kw :label}]`."
  [{:keys [dictionary tp lookup]}]
  (->> (entity-slots dictionary)
       (keep (fn [[slot-kw entry]]
               (let [inst (get tp slot-kw)]
                 (when (and (slot-in-scope? entry lookup (some? inst))
                            (true? (slot-required? entry lookup))
                            (not (slot-done? entry inst)))
                   {:category (slot-category entry)
                    :slot-kw  slot-kw
                    :label    (:slot/label entry)}))))
       vec))

(defn- index-of
  [coll x]
  (or (->> coll (map-indexed vector) (some (fn [[i v]] (when (= v x) i)))) -1))

(defn forward-transition?
  "A transition is forward iff its to-stage is strictly later than its from-stage
   in `stage-ids` order. Backward transitions never gate sub-entities."
  [transition stage-ids]
  (let [from (:transition/from transition)
        to   (:transition/to transition)
        fi   (index-of stage-ids from)
        ti   (index-of stage-ids to)]
    (and (some? from) (some? to) (>= ti 0) (> ti fi))))

(defn blocking-for-transition
  "Filter `incomplete` to the categories gated when entering the transition's
   to-stage (per `stage-gates`), for forward transitions only."
  [{:keys [incomplete transition stage-ids stage-gates]}]
  (if-not (forward-transition? transition stage-ids)
    []
    (let [gates (get stage-gates (:transition/to transition) #{})]
      (filterv #(contains? gates (:category %)) incomplete))))
